import type { AbsolutePosition, Point } from "@hoylu/client-common";
import { Map as ImmutableMap } from "immutable";
import { DateTime } from "luxon";
import type { AjaxError } from "rxjs/ajax";
import { createReducer, isActionOf } from "typesafe-actions";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { Localized } from "../../strings";
import { isElectronMac } from "../../utils/is.electron.mac";
import { resetState } from "../root.action";
import * as templatesActions from "../templates/templates.actions";
import { logout } from "../user/user.actions";
import { defaultRoles } from "./defaultRoles";
import type {
  DashboardDetails,
  Label,
  MyWorkspaceMetadata,
  Project,
  ProjectResponse,
  ProjectUsersResponse,
  ProjectWithUserRole,
  StoredSearchParams,
  TaskLocationsData,
  WorkspaceRoles,
} from "./types";
import * as actions from "./workspaces.actions";

/**
 *  IMPORTANT: When changing the schema version here also update it to the same value in /io-document/src/constants.ts
 */
export const DEFAULT_SCHEMA_VERSION = 3;

export interface WorkspacesState {
  activeOption: DashboardOption | null; // TODO: This should be refactored to contain the entire payload for the modal as well (which is currently spread around the entire WorkspaceState)
  waitingToEditID: string | null;
  waitingToEditAllowTemplateEdit?: boolean;
  waitingToCreateFromTemplate: MyWorkspaceMetadata | null; // TODO: This should really be part of the modal state, not the entire workspace state (as so many other model states in here as well)
  selectedWorkspaceID: string | null;

  // Dashboard Details
  workspaces: MyWorkspaceMetadata[];
  workspacesRoles: {
    [workspaceId: string]: WorkspaceRoles;
  };
  isLoadingWorkspaceDetail: boolean;
  dashboardDetails: DashboardDetails;
  pagination: {
    data: actions.WorkspacesPagingResult | undefined;
    isFetching: boolean;
    fetchFailure: boolean;
  };
  loadProgress?: number;
  loadState?: number;
  dashboardLabels: Label[];
  isFetchingDashboardLabels: boolean;

  // Workspace Searching/Filtering
  searchTerm: string;
  searchLabels: Label[];
  includeHiddenWorkspaces: boolean;

  // Projects
  assignableProjects?: ProjectResponse[];
  projectCollaborators: ProjectUsersResponse[];
  projects?: Map<string, ProjectWithUserRole>; // TODO: Maybe update this to data structure that is JSON-serializable to align with recommended Redux practices.
  selectedProject: Project | null;

  /** Used by sharing dialog to show spinner and to show invite users component */
  isUpdatingWorkspace: boolean;
  isEditingWorkspacePassword: boolean;
  modalTitle?: string;
  modalText?: string;
  modalButtonText?: string;
  modalButtonLinkUrl?: string;
  isCreatingNewWorkspaceUrl?: boolean | null;
  emailPreset?: string;
  isLoadingSelectedProjectWorkspaces: boolean;
  isEditingThumbnail: boolean;
  thumbnailsModified: ImmutableMap<string, Blob | null>; // NOTE: Workaround for abusing augment workspace details refreshment
  tonnageUsage: number;
  showOverlay?: boolean;
  storedSearchParams: StoredSearchParams;
  waitingToLoadProjectID?: string;
  dialogPosition?: Point | AbsolutePosition;
  taskLocationsData?: TaskLocationsData;
}

export const defaultState = (): WorkspacesState => ({
  activeOption: null,
  waitingToEditID: null,
  waitingToCreateFromTemplate: null,
  selectedWorkspaceID: null,

  workspaces: [],
  workspacesRoles: {},
  isLoadingWorkspaceDetail: false,
  dashboardDetails: {
    fullscreenWorkspaceId: null,
    workspaceDetails: {},
  },
  pagination: {
    data: undefined,
    isFetching: false,
    fetchFailure: false,
  },

  dashboardLabels: [],
  isFetchingDashboardLabels: false,
  searchLabels: [],
  searchTerm: "",
  includeHiddenWorkspaces: false,

  isUpdatingWorkspace: false,
  isEditingWorkspacePassword: false,
  loadProgress: 0,
  selectedProject: null,
  projectCollaborators: [],
  loadState: 0,
  isCreatingNewWorkspaceUrl: null,
  isLoadingSelectedProjectWorkspaces: false,
  isEditingThumbnail: false,
  tonnageUsage: 0,
  thumbnailsModified: ImmutableMap<string, Blob | null>(),
  storedSearchParams: {},
  waitingToLoadProjectID: undefined,
  dialogPosition: undefined,
});

export default createReducer<WorkspacesState>(defaultState())
  .handleAction(resetState, () => defaultState())
  .handleAction([logout.success, logout.failure], () => defaultState())
  .handleAction(actions.queueEditWorkspace, (state, action) => {
    /* Temporary workaround to handle issue with endless loading on the Dashboard while opening a hidden Workspace.
       Hidden workspace, which was already loaded to the state.existing[] array is considered as ready to be opened.
       However, when the "hidden" section is closed, the proper WorkspaceCard is missing in the document and endless loading appears.
       Therefore, we open "hidden" section before triggering iFrame to start.
    */

    const isLoadedWorkspaceHidden = state.workspaces.some(
      (workspace) =>
        workspace.workspaceId === action.payload.workspaceId &&
        workspace.isHidden,
    );

    return {
      ...state,
      waitingToEditID: action.payload.workspaceId,
      waitingToEditAllowTemplateEdit: action.payload.allowEditTemplates,
      includeHiddenWorkspaces:
        isLoadedWorkspaceHidden || state.includeHiddenWorkspaces,
      selectedWorkspaceID: null,
      activeOption: null,
    };
  })
  .handleAction(actions.searchParamsForEditWorkspace, (state, action) => ({
    ...state,
    storedSearchParams: action.payload,
  }))
  .handleAction(actions.queueProjectLoading, (state, action) => ({
    ...state,
    waitingToLoadProjectID: action.payload,
  }))
  .handleAction(actions.queueCreateWorkspace, (state) => ({
    ...state,
    isCreatingNewWorkspaceUrl: true,
  }))
  .handleAction(actions.toggleIncludeHiddenWorkspaces, (state) => ({
    ...state,
    includeHiddenWorkspaces: !state.includeHiddenWorkspaces,
  }))
  .handleAction(
    [
      actions.upsertWorkspaceUsers.request,
      actions.removeWorkspaceUsers.request,
    ],
    (state) => ({
      ...state,
      isUpdatingWorkspace: true,
    }),
  )
  .handleAction(
    [
      actions.upsertWorkspaceUsers.failure,
      actions.removeWorkspaceUsers.failure,
    ],
    (state) => ({
      ...state,
      isUpdatingWorkspace: false,
    }),
  )
  .handleAction(
    [
      actions.upsertWorkspaceUsers.success,
      actions.removeWorkspaceUsers.success,
    ],
    (state, action) => ({
      ...state,
      workspacesRoles: {
        ...state.workspacesRoles,
        [action.payload.workspaceId]: action.payload.workspaceRoles,
      },
      isUpdatingWorkspace: false,
    }),
  )
  .handleAction(actions.getProjectCollaborators.success, (state, action) => ({
    ...state,
    projectCollaborators: action.payload,
  }))
  // getProjectCollaborators.request and getProjectCollaborators.failure are ignored as we do not need loading and e handling for now
  .handleAction(actions.updateWorkspaceName.request, (state) => ({
    ...state,
    isUpdatingWorkspace: true,
  }))
  .handleAction(actions.updateWorkspaceName.success, (state, action) => ({
    ...state,
    workspaces: state.workspaces.map((workspace) =>
      workspace.workspaceId === action.payload.workspaceId
        ? {
            ...workspace,
            workspaceName: action.payload.workspaceName,
          }
        : workspace,
    ),
    isUpdatingWorkspace: false,
  }))
  .handleAction(actions.updateWorkspaceName.failure, (state) => ({
    ...state,
    isUpdatingWorkspace: false,
  }))
  .handleAction(templatesActions.deleteTemplate.success, (state, action) => ({
    ...state,
    workspaces: state.workspaces.map((workspace) =>
      workspace.workspaceId === action.payload.workspaceId
        ? {
            ...workspace,
            templateId: null,
          }
        : workspace,
    ),
  }))
  .handleAction(actions.updateWorkspaceGlobalAccess.request, (state) => ({
    ...state,
    isUpdatingWorkspace: true,
  }))
  .handleAction(
    actions.updateWorkspaceGlobalAccess.success,
    (state, action) => ({
      ...state,
      workspacesRoles: {
        ...state.workspacesRoles,
        [action.payload.workspaceId]: {
          ...(state.workspacesRoles[action.payload.workspaceId] ??
            defaultRoles()),
          globalAccess: action.payload.globalAccess,
        },
      },
      isUpdatingWorkspace: false,
    }),
  )
  .handleAction(actions.updateWorkspaceGlobalAccess.failure, (state) => ({
    ...state,
    isUpdatingWorkspace: false,
  }))
  .handleAction(actions.cancelDashboardOption, (state) => ({
    ...state,
    selectedWorkspaceID: null,
    activeOption: null,
    isEditingWorkspacePassword: false,
    isEditingThumbnail: false,
  }))

  // Workspace Pagination
  .handleAction(actions.showDashboard, (state, _action) => ({
    ...state,
    workspaces: [],
    pagination: {
      data: undefined,
      isFetching: false,
      fetchFailure: false,
    },
  }))
  .handleAction(actions.loadWorkspaces.request, (state, action) => {
    return {
      ...state,
      pagination: {
        ...state.pagination,
        isFetching: true,
        fetchFailure: false,
      },
      // NOTE: Clear current workspaces if requesting the first page.
      ...(!action.payload.offset && { workspaces: [] }),
    };
  })
  .handleAction(actions.loadWorkspaces.success, (state, action) => {
    return {
      ...state,
      pagination: {
        ...state.pagination,
        data: action.payload,
        isFetching: false,
      },
      workspaces: state.workspaces
        .filter(
          (w) =>
            !action.payload.workspaces.some(
              (w2) => w.workspaceId === w2.workspaceId,
            ),
        )
        .concat(action.payload.workspaces),
      dashboardDetails: {
        ...state.dashboardDetails,
        workspaceDetails: {
          ...state.dashboardDetails.workspaceDetails,
          ...action.payload.workspaces.reduce<
            DashboardDetails["workspaceDetails"]
          >((acc, workspace) => {
            acc[workspace.workspaceId] = {
              ...acc[workspace.workspaceId],
              workspaceType: workspace.workspaceType,
              workspaceModuleName: workspace.module.name,
              isFromMyWorkspaces: true,
            };
            return acc;
          }, state.dashboardDetails.workspaceDetails),
        },
      },
    };
  })
  .handleAction(actions.loadWorkspaces.failure, (state) => {
    return {
      ...state,
      pagination: {
        ...state.pagination,
        isFetching: false,
        fetchFailure: true,
      },
    };
  })

  // TODO: Figure out if this action and result are actually needed for anything.
  // .handleAction(actions.loadHiddenWorkspaces.success, (state, action) => {
  // })

  .handleAction(actions.getWorkspaceDetails.request, (state) => {
    return {
      ...state,
      isLoadingWorkspaceDetail: true,
    };
  })
  .handleAction(actions.getWorkspaceDetails.success, (state, action) => {
    const existingWorkspace = state.workspaces.find(
      (w) => w.workspaceId === action.payload.workspaceId,
    );

    return {
      ...state,
      workspaces: existingWorkspace
        ? state.workspaces.map((workspace) =>
            workspace.workspaceId === action.payload.workspaceId
              ? action.payload
              : workspace,
          )
        : state.workspaces.concat(action.payload),
      dashboardDetails: {
        ...state.dashboardDetails,
        workspaceDetails: {
          ...state.dashboardDetails.workspaceDetails,
          [action.payload.workspaceId]: {
            ...state.dashboardDetails.workspaceDetails[
              action.payload.workspaceId
            ],
            workspaceModuleName: action.payload.module.name,
            workspaceType: action.payload.workspaceType,
          },
        },
      },
      isLoadingWorkspaceDetail: false,
    };
  })
  .handleAction(actions.getWorkspaceDetails.failure, (state, action) => {
    let unauthorizedError = false;
    if (
      typeof action.payload.error !== "string" &&
      (action.payload.error as AjaxError).status === 401
    ) {
      unauthorizedError = true;
    }
    // close share dialog if there was one open
    const dialogState =
      state.activeOption === DashboardOption.SHARE ||
      state.activeOption === DashboardOption.PERMISSIONS ||
      state.activeOption === DashboardOption.INFO
        ? { activeOption: null, selectedWorkspaceID: null }
        : undefined;
    return {
      ...state,
      ...dialogState,
      waitingToEditID:
        state.waitingToEditID === action.payload.workspaceId &&
        !unauthorizedError
          ? null
          : state.waitingToEditID,
      isLoadingWorkspaceDetail: false,

      ...(!unauthorizedError && {
        workspaces: state.workspaces.filter(
          (workspace) => workspace.workspaceId !== action.payload.workspaceId,
        ),
      }),
    };
  })
  .handleAction(actions.getWorkspaceRoles.success, (state, action) => ({
    ...state,
    workspacesRoles: {
      ...state.workspacesRoles,
      [action.payload.workspaceId]: action.payload.workspaceRoles,
    },
  }))
  .handleAction(actions.atMaxWorkspaces, (state, action) => {
    return {
      ...state,
      waitingToEditID: null,
      workspaces: state.workspaces.filter(
        (workspace) => workspace.workspaceId !== action.payload,
      ),
    };
  })
  .handleAction(actions.makeWorkspaceLive, (state, action) => ({
    ...state,
    dashboardDetails: {
      ...state.dashboardDetails,
      workspaceDetails: {
        ...state.dashboardDetails.workspaceDetails,
        [action.payload]: {
          ...state.dashboardDetails.workspaceDetails[action.payload],
          isLive: true,
        },
      },
    },
  }))
  .handleAction(
    [
      actions.setWorkspacePassword.success,
      actions.removeWorkspacePassword.success,
    ],
    (state, action) => ({
      ...state,
      workspaces: state.workspaces.map((workspace) =>
        workspace.workspaceId === action.payload.workspaceId
          ? {
              ...workspace,
              hasPassword: isActionOf(
                actions.setWorkspacePassword.success,
                action,
              ),
            }
          : workspace,
      ),
    }),
  )
  .handleAction(actions.createWorkspaceFromTemplate, (state, action) => {
    // make sure waitingToEditID is set to null in case this action was executed through queueEditWorkspace
    return {
      ...state,
      waitingToEditID: null,
      waitingToCreateFromTemplate: action.payload,
    };
  })
  .handleAction(actions.editWorkspace, (state, action) => {
    return {
      ...state,
      loadProgress: 0,
      loadState: 0,
      isCreatingNewWorkspaceUrl: null,
      dashboardDetails: {
        ...state.dashboardDetails,
        fullscreenWorkspaceId: action.payload.workspaceId,
        workspaceDetails: {
          ...state.dashboardDetails.workspaceDetails,
          [action.payload.workspaceId]: {
            ...state.dashboardDetails.workspaceDetails[
              action.payload.workspaceId
            ],
            isLive: true,
          },
        },
      },
    };
  })
  .handleAction(actions.setLastAccess, (state, action) => {
    const workspace = state.workspaces.find(
      (workspace) => workspace.workspaceId === action.payload.workspaceId,
    );

    if (!workspace) {
      return state;
    }

    return {
      ...state,
      workspaces: state.workspaces.map((workspace) =>
        workspace.workspaceId === action.payload.workspaceId
          ? {
              ...workspace,
              lastAccess: DateTime.fromISO(action.payload.lastAccess, {
                zone: "utc",
              }).toISO(),
            }
          : workspace,
      ),
    };
  })
  .handleAction(actions.unfocusWorkspaces, (state) => ({
    ...state,
    waitingToEditID: null,
    isCreatingNewWorkspaceUrl: null,
    dashboardDetails: {
      ...state.dashboardDetails,
      fullscreenWorkspaceId: null,
      workspaceDetails: Object.entries(
        state.dashboardDetails.workspaceDetails,
      ).reduce<DashboardDetails["workspaceDetails"]>(
        (acc, [workspaceId, details]) => {
          acc[workspaceId] =
            // NOTE: for killing not fully loaded workspaces - avoid loading multiple in background
            state.dashboardDetails.fullscreenWorkspaceId === workspaceId &&
            !details.isDocumentReady
              ? {
                  ...details,
                  isChannelReady: false,
                  isLive: false,
                }
              : details;
          return acc;
        },
        {},
      ),
    },
  }))
  .handleAction(actions.createWorkspace.success, (state, action) => {
    return {
      ...state,
      activeOption: null,
      workspaces: state.workspaces.concat(action.payload),
      dashboardDetails: {
        ...state.dashboardDetails,
        workspaceDetails: {
          ...state.dashboardDetails.workspaceDetails,
          [action.payload.workspaceId]: {
            ...state.dashboardDetails.workspaceDetails[
              action.payload.workspaceId
            ],
            workspaceModuleName: action.payload.module.name,
            workspaceType: action.payload.workspaceType,
          },
        },
      },
    };
  })
  .handleAction(actions.createWorkspace.failure, (state) => {
    return {
      ...state,
      activeOption: null,
      isCreatingNewWorkspaceUrl: null,
    };
  })
  .handleAction(actions.duplicateWorkspace.success, (state, action) => {
    return {
      ...state,
      activeOption: null,
      workspaces: state.workspaces.concat(action.payload.myWorkspaceMetadata),
      dashboardDetails: {
        ...state.dashboardDetails,
        workspaceDetails: {
          ...state.dashboardDetails.workspaceDetails,
          [action.payload.myWorkspaceMetadata.workspaceId]: {
            ...state.dashboardDetails.workspaceDetails[
              action.payload.myWorkspaceMetadata.workspaceId
            ],
            workspaceModuleName: action.payload.myWorkspaceMetadata.module.name,
            workspaceType: action.payload.myWorkspaceMetadata.workspaceType,
          },
        },
      },
      waitingToCreateFromTemplate: null,
    };
  })
  .handleAction(actions.duplicateWorkspace.failure, (state, action) => {
    if (action.payload.clearError) {
      return {
        ...state,
        activeOption: null,
        selectedWorkspaceID: null,
        message: Localized.string("ERROR.CANNOT_DUPLICATE_WORKSPACE"),
        waitingToCreateFromTemplate: null,
      };
    } else {
      // Incorrect password submitted when trying to duplicate workspace
      return {
        ...state,
        activeOption: state.activeOption,
        selectedWorkspaceID: state.selectedWorkspaceID,
        message: Localized.string("ERROR.INCORRECT_PASSWORD"),
      };
    }
  })
  .handleAction(actions.workspaceChannelReady, (state, action) => ({
    ...state,
    dashboardDetails: {
      ...state.dashboardDetails,
      workspaceDetails: {
        ...state.dashboardDetails.workspaceDetails,
        [action.payload]: {
          ...state.dashboardDetails.workspaceDetails[action.payload],
          isChannelReady: true,
        },
      },
    },
  }))
  .handleAction(actions.workspaceDocumentReady, (state, action) => ({
    ...state,
    dashboardDetails: {
      ...state.dashboardDetails,
      workspaceDetails: {
        ...state.dashboardDetails.workspaceDetails,
        [action.payload]: {
          ...state.dashboardDetails.workspaceDetails[action.payload],
          isDocumentReady: true,
        },
      },
    },
  }))
  .handleAction(actions.toggleWorkspaceVisibility.request, (state) => {
    return {
      ...state,
      activeOption: null,
      selectedWorkspaceID: null,
    };
  })
  .handleAction(actions.toggleWorkspaceVisibility.success, (state, action) => ({
    ...state,
    activeOption: null,
    workspaces: state.workspaces.map((workspace) =>
      workspace.workspaceId === action.payload.workspaceId
        ? {
            ...workspace,
            isHidden: !workspace.isHidden,
          }
        : workspace,
    ),
    dashboardDetails: {
      ...state.dashboardDetails,
      fullscreenWorkspaceId: null, // Close workspace when hiding from web-suite due to breaking Dashboard header and layout
    },
    selectedWorkspaceID: null,
  }))
  .handleAction(actions.toggleWorkspaceVisibility.failure, (state) => {
    return {
      ...state,
      activeOption: null,
      selectedWorkspaceID: null,
    };
  })
  .handleAction(actions.leaveWorkspace.request, (state, action) => {
    return {
      ...state,
      activeOption: null,
      selectedWorkspaceID: null,
      ...(state.dashboardDetails.fullscreenWorkspaceId ===
        action.payload.workspaceId && {
        dashboardDetails: {
          ...state.dashboardDetails,
          fullscreenWorkspaceId: null,
        },
      }),
    };
  })
  .handleAction(actions.leaveWorkspace.success, (state, action) => {
    const dashboardDetails = {
      ...state.dashboardDetails,
      workspaceDetails: {
        ...state.dashboardDetails.workspaceDetails,
      },
    };
    delete dashboardDetails.workspaceDetails[action.payload.workspaceId];

    const workspaces = state.selectedProject
      ? state.workspaces
      : state.workspaces.filter(
          (workspace) => workspace.workspaceId !== action.payload.workspaceId,
        );

    return {
      ...state,
      workspaces,
      dashboardDetails,
    };
  })
  .handleAction(actions.leaveWorkspace.failure, (state) => ({
    ...state,
    activeOption: null,
    selectedWorkspaceID: null,
  }))
  .handleAction([actions.deleteWorkspace.request], (state, action) => {
    return {
      ...state,
      activeOption: null,
      selectedWorkspaceID: null,
      ...(state.dashboardDetails.fullscreenWorkspaceId ===
        action.payload.workspaceId && {
        dashboardDetails: {
          ...state.dashboardDetails,
          fullscreenWorkspaceId: null,
        },
      }),
    };
  })
  .handleAction(actions.deleteWorkspace.success, (state, action) => {
    const dashboardDetails = {
      ...state.dashboardDetails,
      workspaceDetails: {
        ...state.dashboardDetails.workspaceDetails,
      },
    };
    delete dashboardDetails.workspaceDetails[action.payload.workspaceId];

    const workspaces = state.workspaces.filter(
      (workspace) => workspace.workspaceId !== action.payload.workspaceId,
    );

    return {
      ...state,
      workspaces,
      dashboardDetails,
    };
  })
  .handleAction([actions.deleteWorkspace.failure], (state) => ({
    ...state,
    activeOption: null,
    selectedWorkspaceID: null,
  }))
  .handleAction(actions.activateDashboardOption, (state, action) => {
    switch (action.payload.optionType) {
      case DashboardOption.SEARCH_WORKSPACE:
        return {
          ...state,
          activeOption: action.payload.optionType,
        };
      case DashboardOption.CREATE:
        return {
          ...state,
          activeOption: DashboardOption.CREATE,
        };
      case DashboardOption.NEXUS_INSIGHTS_PANEL:
        return {
          ...state,
          activeOption: DashboardOption.NEXUS_INSIGHTS_PANEL,
        };
      case DashboardOption.HIDE:
        return {
          ...state,
          activeOption: DashboardOption.HIDE,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.LEAVE:
        return {
          ...state,
          activeOption: DashboardOption.LEAVE,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.DELETE:
        return {
          ...state,
          activeOption: DashboardOption.DELETE,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.DROP_SCOPE:
        return {
          ...state,
          activeOption: DashboardOption.DROP_SCOPE,
        };
      case DashboardOption.REMOVE:
        return {
          ...state,
          activeOption: DashboardOption.REMOVE,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.REMOVE_FROM_PROJECT:
        return {
          ...state,
          activeOption: DashboardOption.REMOVE_FROM_PROJECT,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.SHARE:
      case DashboardOption.INFO:
      case DashboardOption.PERMISSIONS:
      case DashboardOption.EXPANDED_SHARE:
        return {
          ...state,
          activeOption: action.payload.optionType,
          selectedWorkspaceID: action.payload.workspaceId!,
          emailPreset: action.payload.emailPreset,
          showOverlay: action.payload.showOverlay,
        };
      case DashboardOption.NOTIFICATION_CENTER:
        if (state.activeOption === action.payload.optionType) {
          return {
            ...state,
            activeOption: null,
            selectedWorkspaceID: null,
          };
        }

        return {
          ...state,
          activeOption: DashboardOption.NOTIFICATION_CENTER,
          selectedWorkspaceID: action.payload.workspaceId!,
          dialogPosition: action.payload.dialogPosition,
        };
      case DashboardOption.ADD_LABEL:
        return {
          ...state,
          activeOption: DashboardOption.ADD_LABEL,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.DUPLICATE:
        return {
          ...state,
          activeOption: DashboardOption.DUPLICATE,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.CREATE_FROM_TEMPLATE:
        if (!state.waitingToCreateFromTemplate) return state; // Don't activate the option if state for model is not set up
        return {
          ...state,
          activeOption: DashboardOption.CREATE_FROM_TEMPLATE,
        };
      case DashboardOption.ADVANCED_SHARE:
        return {
          ...state,
          activeOption: DashboardOption.ADVANCED_SHARE,
        };
      case DashboardOption.OPEN:
        return {
          ...state,
          activeOption: DashboardOption.OPEN,
        };
      case DashboardOption.OPTIONS:
      case DashboardOption.OPTIONS_FAVORITE:
        if (
          state.activeOption === action.payload.optionType &&
          state.selectedWorkspaceID === action.payload.workspaceId
        ) {
          return {
            ...state,
            activeOption: null,
            selectedWorkspaceID: null,
          };
        }
        return {
          ...state,
          activeOption: action.payload.optionType,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.INVITE:
        return {
          ...state,
          activeOption: DashboardOption.INVITE,
        };
      case DashboardOption.UPSELL: {
        let title = action.payload.modalOptions?.modalTitle;
        let text = action.payload.modalOptions?.modalText;
        let buttonText = action.payload.modalOptions?.modalButtonText;
        const buttonLinkUrl = action.payload.modalOptions?.modalButtonLinkUrl;
        if (
          isElectronMac() &&
          buttonLinkUrl === "https://www.hoylu.com/pricing/"
        ) {
          title = Localized.string("MAC_APP.UPSELL_TITLE");
          text = Localized.string("MAC_APP.UPSELL_TEXT");
          buttonText = Localized.string("MAC_APP.UPSELL_BUTTON"); // button is hidden for MacApp
        }

        return {
          ...state,
          activeOption: DashboardOption.UPSELL,
          modalTitle: title,
          modalText: text,
          modalButtonText: buttonText,
          modalButtonLinkUrl: buttonLinkUrl,
          waitingToEditID: null,
          waitingToEditAllowTemplateEdit: undefined,
          isCreatingNewWorkspaceUrl: null,
        };
      }
      case DashboardOption.ASSET_PANEL:
        return {
          ...state,
          activeOption: DashboardOption.ASSET_PANEL,
        };
      case DashboardOption.DOWNLOAD:
        return {
          ...state,
          activeOption: DashboardOption.DOWNLOAD,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      case DashboardOption.UPLOAD:
        return {
          ...state,
          activeOption: DashboardOption.UPLOAD,
        };
      case DashboardOption.MANAGE_ACCOUNT: {
        return { ...state, activeOption: DashboardOption.MANAGE_ACCOUNT };
      }
      case DashboardOption.CONFIRM_ACCOUNT_DELETE: {
        return {
          ...state,
          activeOption: DashboardOption.CONFIRM_ACCOUNT_DELETE,
        };
      }
      case DashboardOption.LINKED_MASTER:
      case DashboardOption.CHANGE_MASTER:
      case DashboardOption.DELETE_TEMPLATE: {
        return {
          ...state,
          activeOption: action.payload.optionType,
          selectedWorkspaceID: action.payload.workspaceId!,
        };
      }
      case DashboardOption.FILTER:
        return {
          ...state,
          activeOption: DashboardOption.FILTER,
        };
      case DashboardOption.SHOW_TASK_LOCATIONS:
        return {
          ...state,
          activeOption: DashboardOption.SHOW_TASK_LOCATIONS,
          taskLocationsData: action.payload.taskLocationsData,
          dialogPosition: action.payload.dialogPosition,
        };
      case DashboardOption.ANNOUNCEMENTS:
        return {
          ...state,
          activeOption: DashboardOption.ANNOUNCEMENTS,
        };
      default:
        return state;
    }
  })
  .handleAction(actions.workspaceProgressUpdate, (state, action) => {
    if (
      (state.loadProgress || 0) < action.payload.progress ||
      state.loadState !== action.payload.progressStep
    ) {
      return {
        ...state,
        loadProgress: action.payload.progress,
        loadState: action.payload.progressStep,
      };
    }
    return state;
  })
  .handleAction(actions.updateWorkspaceLabels.success, (state, action) => {
    return {
      ...state,
      workspaces: state.workspaces.map((workspace) =>
        workspace.workspaceId === action.payload.workspaceId
          ? {
              ...workspace,
              labels: action.payload.labels,
            }
          : workspace,
      ),
    };
  })
  .handleAction(actions.updateWorkspaceLabels.failure, (state) => {
    return state.activeOption === DashboardOption.ADD_LABEL
      ? {
          ...state,
          activeOption: null,
          selectedWorkspaceID: null,
        }
      : state;
  })
  .handleAction(actions.assignableProjects.success, (state, action) => ({
    ...state,
    assignableProjects: action.payload ?? undefined,
  }))
  .handleAction(actions.assignableProjects.failure, (state) => ({
    ...state,
    assignableProjects: undefined,
  }))
  .handleAction(actions.getMyTonnage.success, (state, action) => ({
    ...state,
    tonnageUsage: Math.round(action.payload.tonnagePercentageUsed),
  }))
  // TODO: Where should we save different user tonnage?
  // .handleAction(actions.getUserTonnage.success, (state) => ({
  //   ...state,
  // }))
  // TODO: Handle recalculation
  // .handleAction(actions.recalculateUserTonnage.success, (state) => ({
  //   ...state,
  // }))
  .handleAction(actions.getProjectsOfUser.success, (state, action) => ({
    ...state,
    projects: action.payload
      ? new Map(action.payload.map((p) => [p.id, p]))
      : undefined,
  }))
  .handleAction(actions.addWorkspaceToProject.success, (state, action) => ({
    ...state,
    workspaces: state.workspaces.map((workspace) =>
      workspace.workspaceId === action.payload.workspaceId
        ? {
            ...workspace,
            containerId: action.payload.projectId,
          }
        : workspace,
    ),
  }))
  .handleAction(
    actions.removeWorkspaceFromProject.success,
    (state, action) => ({
      ...state,
      workspaces: state.workspaces.map((workspace) =>
        workspace.workspaceId === action.payload.workspaceId
          ? {
              ...workspace,
              containerId: null,
            }
          : workspace,
      ),
    }),
  )
  .handleAction(actions.getWorkspacesOfProject.request, (state) => ({
    ...state,
    isLoadingSelectedProjectWorkspaces: true,
  }))
  .handleAction(actions.getWorkspacesOfProject.success, (state, action) => ({
    ...state,
    workspaces: action.payload,
    dashboardDetails: {
      ...state.dashboardDetails,
      workspaceDetails: {
        ...state.dashboardDetails.workspaceDetails,
        ...action.payload.reduce<DashboardDetails["workspaceDetails"]>(
          (acc, workspace) => {
            acc[workspace.workspaceId] = {
              ...acc[workspace.workspaceId],
              workspaceType: workspace.workspaceType,
              workspaceModuleName: workspace.module.name,
              isFromMyWorkspaces: false,
            };
            return acc;
          },
          state.dashboardDetails.workspaceDetails,
        ),
      },
    },
    isLoadingSelectedProjectWorkspaces: false,
  }))
  .handleAction(actions.getWorkspacesOfProject.failure, (state) => ({
    ...state,
    isLoadingSelectedProjectWorkspaces: false,
  }))
  .handleAction(actions.editThumbnail, (state, action) => ({
    ...state,
    isEditingThumbnail: action.payload.flag,
  }))
  .handleAction(actions.setSearchTerm, (state, action) => ({
    ...state,
    searchTerm: action.payload,
  }))
  .handleAction(actions.setSearchLabels, (state, action) => ({
    ...state,
    searchLabels: action.payload,
  }))
  .handleAction(actions.getDashboardLabels.request, (state) => ({
    ...state,
    isFetchingDashboardLabels: true,
  }))
  .handleAction(actions.getDashboardLabels.success, (state, action) => ({
    ...state,
    dashboardLabels: action.payload,
    isFetchingDashboardLabels: false,
  }))
  .handleAction(actions.getDashboardLabels.failure, (state) => ({
    ...state,
    dashboardLabels: [],
    isFetchingDashboardLabels: false,
  }))
  .handleAction(
    [actions.uploadThumbnail.request, actions.removeThumbnail.request],
    (state, action) => {
      const isAddingThumbnail = isActionOf(
        [actions.uploadThumbnail.request],
        action,
      );

      return {
        ...state,
        thumbnailsModified: isAddingThumbnail
          ? state.thumbnailsModified.set(action.payload.workspaceId, null)
          : state.thumbnailsModified.remove(action.payload.workspaceId),
        activeOption: null,
        isEditingThumbnail: false,
      };
    },
  )
  .handleAction(
    [actions.uploadThumbnail.success, actions.removeThumbnail.success],
    (state, action) => {
      const isAddedThumbnail = isActionOf(
        [actions.uploadThumbnail.success],
        action,
      );

      return {
        ...state,
        thumbnailsModified: isAddedThumbnail
          ? state.thumbnailsModified.set(action.payload.workspaceId, null)
          : state.thumbnailsModified.remove(action.payload.workspaceId),
        activeOption: DashboardOption.INFO,
        isEditingThumbnail: false,
        selectedWorkspaceID: action.payload.workspaceId,
        dashboardDetails: {
          ...state.dashboardDetails,
          workspaceDetails: {
            ...state.dashboardDetails.workspaceDetails,
            [action.payload.workspaceId]: {
              ...state.dashboardDetails.workspaceDetails[
                action.payload.workspaceId
              ],
              thumbnailUrl: isAddedThumbnail ? action.payload.url : undefined,
              thumbnailChanged:
                !state.dashboardDetails.workspaceDetails[
                  action.payload.workspaceId
                ]?.thumbnailChanged,
            },
          },
        },
      };
    },
  )
  .handleAction(actions.workspaceSnapshot.request, (state) => ({
    ...state,
    activeOption: DashboardOption.REQUESTING_SNAPSHOT,
    isEditingThumbnail: false,
  }))
  .handleAction(actions.workspaceSnapshot.success, (state, action) => ({
    ...state,
    thumbnailsModified: state.thumbnailsModified.set(
      action.payload.workspaceId,
      action.payload.snapshot,
    ),
    activeOption: DashboardOption.INFO,
    selectedWorkspaceID: action.payload.workspaceId,
    isEditingThumbnail: true,
  }))
  .handleAction(actions.workspaceSnapshot.failure, (state) => ({
    ...state,
    activeOption: DashboardOption.INFO,
    isEditingThumbnail: false,
  }))
  .handleAction(actions.selectProject, (state, action) => ({
    ...state,
    selectedProject: action.payload,
    searchLabels: [],
    searchTerm: "",
    includeHiddenWorkspaces: false,
    workspaces: [],
    pagination: {
      data: undefined,
      isFetching: false,
      fetchFailure: false,
    },
  }));
